import { ticketsApi } from "#/api/ticketsApi";
import { showSuccessNotification } from "#/components/notifications";
import type { CreateTicketPayload, Ticket } from "#/types/tickets";
import { TicketStatus } from "#/types/tickets";
import {
  closestCenter,
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { Button, Modal, Textarea } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconDeviceFloppy } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { logger } from "#/utils/logger";
import { useTranslation } from "react-i18next";
import KanbanColumn from "./KanbanColumn";
import { TicketPreviewCard } from "./TicketPreviewCard";

type TicketColumns = {
  [key in TicketStatus]: Ticket[];
};

/** Where a dragged card came from and where it was dropped. */
interface DropTarget {
  ticketId: number;
  sourceKey: TicketStatus;
  sourceIndex: number;
  destKey: TicketStatus;
  destIndex: number;
}

export interface Props {
  loading: boolean;
  tickets: Ticket[];
  /**
   * False while a status filter hides part of each column: a local index then
   * says nothing about the real position, so cards can only change column (and
   * land at its bottom), not be reordered.
   */
  reorderEnabled: boolean;
}

const isColumnKey = (value: string): value is TicketStatus =>
  (Object.values(TicketStatus) as string[]).includes(value);

const emptyColumns = (): TicketColumns => ({
  [TicketStatus.Requested]: [],
  [TicketStatus.InProgress]: [],
  [TicketStatus.Done]: [],
  [TicketStatus.ProblemTickets]: [],
});

/** Finds the column and index a ticket currently sits in. */
const locateTicket = (
  cols: TicketColumns,
  ticketId: number
): { key: TicketStatus; index: number } | null => {
  for (const key of Object.keys(cols) as TicketStatus[]) {
    const index = cols[key].findIndex((item) => item.id === ticketId);
    if (index !== -1) return { key, index };
  }
  return null;
};

/**
 * Returns a copy of `cols` with `ticket` removed from every column and inserted
 * into `destKey` at `destIndex` (clamped to the column length). Removing first
 * then inserting gives the same result as `arrayMove` for same-column moves.
 */
const placeTicket = (
  cols: TicketColumns,
  ticket: Ticket,
  destKey: TicketStatus,
  destIndex: number
): TicketColumns => {
  const next = emptyColumns();
  for (const key of Object.keys(cols) as TicketStatus[]) {
    next[key] = cols[key].filter((item) => item.id !== ticket.id);
  }
  const end = next[destKey].length;
  // A non-integer index (e.g. an older backend not returning `position`) goes to the end.
  const clampedIndex = Number.isInteger(destIndex) ? Math.max(0, Math.min(destIndex, end)) : end;
  next[destKey].splice(clampedIndex, 0, ticket);
  return next;
};

export const KanbanBoard = ({ loading = true, tickets, reorderEnabled }: Props) => {
  const { t } = useTranslation();
  const [columns, setColumns] = useState<TicketColumns>(emptyColumns);
  const [activeItem, setActiveItem] = useState<Ticket | null>(null);
  const [updatingTicketId, setUpdatingTicketId] = useState<number | null>(null);
  const [overColumnKey, setOverColumnKey] = useState<string | null>(null);
  // Drop into the Problem column waiting for the user to type a reason.
  const [pendingProblemDrop, setPendingProblemDrop] = useState<DropTarget | null>(null);

  const problemForm = useForm({
    initialValues: { problem_details: "" },
    validate: {
      problem_details: (value) =>
        value.trim() ? null : t("tickets.card.reasonRequired"),
    },
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;

    if (!over) {
      setOverColumnKey(null);
      return;
    }

    const overId = over.id.toString();

    if (isColumnKey(overId)) {
      setOverColumnKey(overId);
      return;
    }

    setOverColumnKey(locateTicket(columns, Number(overId))?.key ?? null);
  };

  const handleDragStart = (event: DragStartEvent) => {
    const ticketId = Number(event.active.id);
    const found = Object.values(columns)
      .flat()
      .find((i) => i.id === ticketId);
    if (found) setActiveItem(found);
  };

  /**
   * Resolves a drop into `{ sourceKey, sourceIndex, destKey, destIndex }`.
   * `destIndex` is the index the card will occupy in the destination column:
   * the index of the card it was dropped on, or the end of the column when it
   * was dropped on the column itself (which is also what an empty column
   * reports). Returns null when the drop cannot be resolved or is a no-op.
   */
  const resolveDrop = (event: DragEndEvent): DropTarget | null => {
    const { active, over } = event;
    if (!over) return null;

    const ticketId = Number(active.id);
    const source = locateTicket(columns, ticketId);
    if (!source) return null;

    const overId = over.id.toString();
    let destKey: TicketStatus;
    let destIndex: number;

    if (isColumnKey(overId)) {
      destKey = overId;
      // Last slot of the column; one less when the card is already in it,
      // because it is removed before being re-inserted.
      destIndex =
        destKey === source.key
          ? columns[destKey].length - 1
          : columns[destKey].length;
    } else {
      const overTicket = locateTicket(columns, Number(overId));
      if (!overTicket) return null;
      destKey = overTicket.key;
      destIndex = overTicket.index;
    }

    if (destKey === source.key && (destIndex === source.index || !reorderEnabled)) return null;

    // Filtered board: the column is only partially shown, so the card goes to
    // the bottom of its new column instead of a made-up index.
    if (!reorderEnabled) destIndex = columns[destKey].length;

    return {
      ticketId,
      sourceKey: source.key,
      sourceIndex: source.index,
      destKey,
      destIndex,
    };
  };

  /**
   * Applies the drop locally and persists it. `position` is the card's local
   * index; the backend re-numbers neighbours around it. With a status filter
   * active no position is sent (see `reorderEnabled`).
   */
  const commitDrop = (drop: DropTarget, problemDetails?: string) => {
    // Located by id rather than by the resolved index: the dialog path commits
    // later, and a pending request may have re-placed cards in the meantime.
    const current = locateTicket(columns, drop.ticketId);
    if (!current) return;
    const ticket = columns[current.key][current.index];

    setColumns(placeTicket(columns, ticket, drop.destKey, drop.destIndex));

    const statusChanged = drop.destKey !== current.key;
    updateTicket(
      ticket.id,
      {
        // Without a position the backend appends to the bottom of the column.
        ...(reorderEnabled ? { position: drop.destIndex } : {}),
        ...(statusChanged ? { status: drop.destKey } : {}),
        ...(problemDetails !== undefined ? { problem_details: problemDetails } : {}),
      },
      columns
    );
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setOverColumnKey(null);
    setActiveItem(null);

    const drop = resolveDrop(event);
    if (!drop) return;

    // Entering the Problem column needs a reason first; the card stays put
    // until the dialog is confirmed. Reordering inside Problem does not ask.
    if (
      drop.destKey === TicketStatus.ProblemTickets &&
      drop.sourceKey !== TicketStatus.ProblemTickets
    ) {
      problemForm.reset();
      setPendingProblemDrop(drop);
      return;
    }

    commitDrop(drop);
  };

  const closeProblemDialog = () => {
    setPendingProblemDrop(null);
    problemForm.reset();
  };

  const handleProblemConfirm = (values: typeof problemForm.values) => {
    if (!pendingProblemDrop) return;
    const drop = pendingProblemDrop;
    closeProblemDialog();
    commitDrop(drop, values.problem_details.trim());
  };

  /**
   * Persists a ticket change. `snapshot` is the column state before the
   * optimistic update; on failure the ticket is put back where the snapshot
   * had it (only that ticket is reverted so other in-flight drags survive).
   * On success the server's ticket replaces the local one: in place when it is
   * already in the right column, otherwise at the `position` the server returns.
   */
  const updateTicket = (
    id: number,
    payload: Partial<CreateTicketPayload>,
    snapshot: TicketColumns = columns
  ) => {
    setUpdatingTicketId(id);
    ticketsApi
      .update(id, payload)
      .then((res) => {
        showSuccessNotification({
          message: t("tickets.updatedSuccess"),
        });

        const updatedTicket = res.data;
        setColumns((prev) => {
          const current = locateTicket(prev, updatedTicket.id);
          const index =
            current && current.key === updatedTicket.status
              ? current.index
              : updatedTicket.position;
          return placeTicket(prev, updatedTicket, updatedTicket.status, index);
        });
      })
      .catch((err) => {
        // The global axios interceptor already toasts the error.
        logger.error("Failed to update ticket", err);
        const previous = locateTicket(snapshot, id);
        if (!previous) return;
        const ticket = snapshot[previous.key][previous.index];
        setColumns((prev) => placeTicket(prev, ticket, previous.key, previous.index));
      })
      .finally(() => {
        setUpdatingTicketId(null);
      });
  };

  useEffect(() => {
    const newColumns = emptyColumns();

    for (const ticket of tickets) {
      if (ticket.status === TicketStatus.Requested)
        newColumns[TicketStatus.Requested].push(ticket);
      else if (ticket.status === TicketStatus.InProgress)
        newColumns[TicketStatus.InProgress].push(ticket);
      else if (ticket.status === TicketStatus.Done)
        newColumns[TicketStatus.Done].push(ticket);
      else newColumns[TicketStatus.ProblemTickets].push(ticket);
    }

    setColumns(newColumns);
  }, [tickets]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
    >
      <div className="w-full h-full flex gap-4 overflow-x-auto lg:grid lg:grid-cols-[repeat(4,minmax(275px,1fr))]">
        {Object.entries(columns).map(([key, items]) => (
          <KanbanColumn
            key={key}
            keyId={key as TicketStatus}
            items={items}
            isOver={overColumnKey === key}
            loading={loading}
            updatingTicketId={updatingTicketId}
            updateTicket={updateTicket}
          />
        ))}
      </div>

      <DragOverlay>
        {activeItem ? <TicketPreviewCard ticket={activeItem} /> : null}
      </DragOverlay>

      <Modal
        opened={pendingProblemDrop !== null}
        onClose={closeProblemDialog}
        centered
        size="lg"
        title={t("tickets.problemDialog.title")}
        closeOnClickOutside={false}
        classNames={{
          title: "font-bold",
        }}
      >
        <form
          className="w-full flex flex-col gap-5"
          onSubmit={problemForm.onSubmit(handleProblemConfirm)}
        >
          <div className="flex flex-col gap-3 text-sm">
            <span>{t("tickets.problemDialog.description")}</span>
            <Textarea
              label={t("tickets.card.problemDetails")}
              placeholder={t("tickets.card.problemPlaceholder")}
              withAsterisk
              autoFocus
              autosize
              minRows={3}
              {...problemForm.getInputProps("problem_details")}
            />
          </div>

          <div className="w-full grid grid-cols-[auto_1fr] items-center gap-2">
            <Button size="sm" color="black" type="button" onClick={closeProblemDialog}>
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              color="red"
              size="sm"
              leftSection={<IconDeviceFloppy size={18} />}
            >
              {t("tickets.problemDialog.confirmButton")}
            </Button>
          </div>
        </form>
      </Modal>
    </DndContext>
  );
};
